import { CITIES } from "./cities";
import { SERVICES, IMAGES } from "./services";
import { COUNTIES, getCounty } from "./counties";
import { INSIGHTS, getInsight } from "./insights";
import { TESTIMONIALS } from "./testimonials";

export { CITIES, SERVICES, IMAGES, COUNTIES, getCounty, INSIGHTS, getInsight, TESTIMONIALS };

export const SITE_URL = "https://northernirelandsecurity.co.uk";
export const PHONE = "+44 2046 402840";
export const PHONE_HREF = "tel:+442046402840";
export const EMAIL = "admin@xtremeprotection.uk";
export const EMAIL_HREF = "mailto:admin@xtremeprotection.uk";

const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const pick = (arr, seed) => arr[seed % arr.length];

export const getCity = (slug) => CITIES.find((c) => c.slug === slug);
export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

const INTRO_TEMPLATES = [
  (c, s) =>
    `Looking for reliable ${s.shortName} in ${c.name}? Xtreme Protection is one of Northern Ireland's most trusted security companies, deploying SIA-licensed officers across ${c.county} and beyond. Our ${s.shortName} teams protect businesses throughout ${c.hubs[0]}, ${c.hubs[1]} and every surrounding district — with every shift verified, reported and backed by our 24/7 control room.`,
  (c, s) =>
    `${c.name} businesses choose Xtreme Protection for ${s.shortName} because we combine local knowledge with national-standard compliance. Every officer we deploy in ${c.county} is SIA licensed, BS7858 vetted and briefed on the specific risks facing sites around ${c.hubs[0]} and ${c.hubs[2] || c.hubs[1]}. From first risk assessment to daily digital reporting, our ${s.shortName} service is built to be accountable.`,
  (c, s) =>
    `When ${c.name} needs professional ${s.shortName}, the answer is a security partner that knows the area. Xtreme Protection supplies vetted, licensed ${s.shortName} officers across ${c.county}, covering ${c.hubs[0]}, ${c.hubs[1]} and the wider ${c.name} area with verified patrols, real-time reporting and rapid escalation whenever it is needed.`,
  (c, s) =>
    `Xtreme Protection delivers ${s.shortName} in ${c.name} to the standard Northern Ireland businesses expect: SIA-licensed officers, BS7858 vetting and a 24/7 control room behind every shift. Whether your premises sit near ${c.hubs[0]} or on the edge of ${c.county}, our local teams protect them as if they were our own.`,
  (c, s) =>
    `Professional ${s.shortName} in ${c.name} should never be a gamble. Xtreme Protection assigns locally recruited, fully licensed officers to sites across ${c.county} — from ${c.hubs[0]} to ${c.hubs[1]} — with written assignment instructions, electronic patrol verification and same-day deployment available for urgent requirements.`,
  (c, s) =>
    `From single-site cover to multi-location contracts, Xtreme Protection provides ${s.shortName} across ${c.name} and the rest of ${c.county}. Our officers protect premises around ${c.hubs[0]}, ${c.hubs[2] || c.hubs[1]} and beyond, supported by GPS-verified reporting and a Northern Ireland control room that never closes.`,
];

const LOCAL_HEADINGS = [
  "The local security landscape",
  "Understanding local risk",
  "Security challenges in the area",
  "Why local knowledge matters",
];

const LOCAL_TEMPLATES = [
  (c, s) =>
    `${c.local} That is exactly the environment our ${s.shortName} teams are trained for — visible deterrence, fast escalation and evidence-grade reporting on every shift.`,
  (c, s) =>
    `${c.local} Xtreme Protection's ${s.shortName} service in ${c.name} is designed around these realities, not a generic national template.`,
  (c, s) =>
    `${c.local} Our ${s.shortName} officers work these districts daily, so nothing about your site's surroundings is unfamiliar to them.`,
];

const DELIVERY_HEADINGS = [
  "How we deliver this service",
  "What our service includes",
  "Our approach on your site",
  "Deployment, done properly",
];

const CLOSING_TEMPLATES = [
  (c, s) =>
    `Ready to discuss ${s.shortName} in ${c.name}? Call our Northern Ireland team on ${PHONE} or email ${EMAIL} for a free, no-obligation risk assessment and a tailored quotation for your premises.`,
  (c, s) =>
    `Every ${s.shortName} contract in ${c.name} starts with a free site risk assessment. Speak to Xtreme Protection today on ${PHONE} or email ${EMAIL} — we typically respond the same working day.`,
  (c, s) =>
    `If you are comparing ${s.shortName} providers in ${c.name}, ask us for a quotation. You will get transparent pricing, licensed officers and verifiable reporting. Call ${PHONE} or email ${EMAIL} to get started.`,
  (c, s) =>
    `Protecting ${c.name} businesses is what we do. For ${s.shortName} anywhere in ${c.county}, contact Xtreme Protection on ${PHONE} or ${EMAIL} and we will build a security plan around your exact risks.`,
  (c, s) =>
    `One call is all it takes to put professional ${s.shortName} in place at your ${c.name} premises. Ring ${PHONE} or email ${EMAIL} and our team will take care of the rest — assessment, deployment and reporting.`,
];

const ANCHOR_VARIANTS = [
  (s, c) => `${s.title} in ${c.name}`,
  (s, c) => `${c.name} ${s.shortName}`,
  (s, c) => `${s.shortName} services ${c.name}`,
  (s, c) => `${s.title} — ${c.name}`,
  (s, c) => `${s.shortName} company in ${c.name}`,
  (s, c) => `hire ${s.shortName} in ${c.name}`,
];

export const comboAnchor = (service, city) => {
  const seed = hash(service.slug + "|" + city.slug);
  return pick(ANCHOR_VARIANTS, seed >>> 2)(service, city);
};

export function comboContent(city, service) {
  const seed = hash(city.slug + ":" + service.slug);
  const i1 = seed % 3;
  const i2 = (i1 + 1 + ((seed >>> 5) % 2)) % 3;
  const f1 = (seed >>> 3) % 3;
  const f2 = (f1 + 1 + ((seed >>> 7) % 2)) % 3;
  return {
    title: `${service.title} in ${city.name} | Xtreme Protection`,
    description: `SIA-licensed ${service.shortName} in ${city.name}, ${city.county}. ${service.tagline}. Call ${PHONE} for a free risk assessment.`,
    h1: `${service.title} in ${city.name}`,
    intro: pick(INTRO_TEMPLATES, seed)(city, service),
    localHeading: pick(LOCAL_HEADINGS, seed >>> 3),
    local: pick(LOCAL_TEMPLATES, seed >>> 2)(city, service),
    deliveryHeading: pick(DELIVERY_HEADINGS, seed >>> 4),
    paras: [service.paras[i1], service.paras[i2]],
    closing: pick(CLOSING_TEMPLATES, seed >>> 6)(city, service),
    faqs: [
      service.faqs[f1],
      {
        q: `Do your ${service.shortName} officers cover all areas of ${city.name}?`,
        a: `Yes — we provide ${service.shortName} across the whole of ${city.name}, including ${city.hubs[0]}, ${city.hubs[1]}, ${city.hubs[2] || city.hubs[0]} and every surrounding district of ${city.county}.`,
      },
      service.faqs[f2],
      {
        q: `How do I hire ${service.shortName} in ${city.name}?`,
        a: `Call ${PHONE} or email ${EMAIL}. We will carry out a free risk assessment of your ${city.name} premises and provide a tailored, no-obligation quotation — urgent deployments can often begin the same day.`,
      },
    ],
  };
}

export function cityFaqs(city) {
  return [
    {
      q: `Which areas of ${city.name} do your security guards cover?`,
      a: `Our officers cover all of ${city.name} and ${city.county}, including ${city.hubs.slice(0, 4).join(", ")} and the surrounding districts. Multi-site coverage across the area is available on a single contract.`,
    },
    {
      q: `Are your ${city.name} security officers SIA licensed?`,
      a: "Yes. Every frontline officer holds a valid SIA licence, is vetted to BS7858 standard and is site-briefed before deployment. Licence details are supplied with every contract.",
    },
    {
      q: `Can you provide security for both businesses and events in ${city.name}?`,
      a: `Yes — Xtreme Protection provides manned guarding, mobile patrols, CCTV monitoring, door supervision, event security, retail security, construction site security, key holding & alarm response, close protection and corporate security across ${city.name}.`,
    },
    {
      q: `How do I get a security quotation in ${city.name}?`,
      a: `Call ${PHONE} or email ${EMAIL}. We arrange a free risk assessment of your premises and provide a tailored quotation — emergency cover in ${city.name} can often begin the same day.`,
    },
  ];
}

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: `${SITE_URL}${it.to === "/" ? "/" : it.to}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const provider = {
  "@type": "LocalBusiness",
  name: "Xtreme Protection — Northern Ireland Security Services",
  url: SITE_URL,
  telephone: PHONE,
  email: EMAIL,
};

export const serviceLd = (service, areaName) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: areaName ? `${service.title} in ${areaName}` : `${service.title} Northern Ireland`,
  serviceType: service.title,
  provider,
  areaServed: { "@type": areaName ? "City" : "AdministrativeArea", name: areaName || "Northern Ireland" },
  description: service.short,
});
